import { useEffect, useState, useRef } from 'react';
import { CampaignCardProps } from './campaign-card-types';
import { CampaignCardFooter } from './components/campaign-card-footer';
import { CardContainer, ImageContainer, CardImage, CardTitle } from './style-component'
import { useNavigate } from 'react-router-dom';
import { getCampaignMissionProgressCount } from '@/services/campaign/campaign-progress-count/campaign-progress-count';
import SkeletonLoader from '../skeleton-loader/skeleton-loader';
import { useUserContext } from '@/contexts/user/useUserContext';
import { CampaignProgressCount } from '@/types/campaign/campaign-progress-count/campaign-progress-count-type';
import useFetch from '@/hook/useFetch/useFetch';
import SparksOverlay from '../sparks-overlay/sparks-overlay';
import { getTotalMissionsBasedOnEnv } from '@/utils/total-mission-based-on-env';

export const CampaignCard: React.FC<CampaignCardProps> = ({
  title,
  imageUrl,
  start_date,
  end_date,
  id,
  highlight,
}) => {
  const navigate = useNavigate();
  const isDragging = useRef(false);
  const { user, isConnect } = useUserContext();
  const [missionProgress, setMissionProgress] = useState<CampaignProgressCount>({
    total_missions: 0,
    completed_missions: 0
  });
  const isCompleted = missionProgress?.completed_missions > 0 && missionProgress?.total_missions > 0 && missionProgress?.completed_missions >= missionProgress?.total_missions;
  const { data, loading, startFetch } = useFetch(getCampaignMissionProgressCount, { user_main_key: user?.main_key ?? null, campaign_id: id }, { autoFetch: true});
  const firstRender = useRef<boolean>(true);
  useEffect(() => {
    if (isConnect && user?.main_key && firstRender.current) {
      firstRender.current = false;
      startFetch();
    }
  }, [isConnect, startFetch, user]);

  useEffect(() => {
    if (data?.total_missions || data?.completed_missions) {
      setMissionProgress({
        total_missions: data?.total_missions,
        completed_missions: data?.completed_missions
      });
    }
  }, [data]);

  const handleMouseDown = () => {
    isDragging.current = false;
  };

  const handleMouseMove = () => {
    isDragging.current = false;
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!isDragging.current) {
      navigate(`/campaign/${id}`);
    }
  }

  return (
    <CardContainer
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
    >
      <ImageContainer $highlighted={highlight}>
        {highlight && <SparksOverlay />}
        <CardImage src={imageUrl} alt={title} />
      </ImageContainer>
      <CardTitle>{title}</CardTitle>
      {loading ? (
        <SkeletonLoader data-testid="mission-progress-skeleton" paragraph={false} title={true} />
      ) : (
        <CampaignCardFooter
          campaignId={id}
          endDate={end_date}
          startDate={start_date}
          rewards={{
            current: missionProgress?.completed_missions,
            total: getTotalMissionsBasedOnEnv(id, missionProgress?.total_missions)
          }}
          isCompleted={isConnect && isCompleted}
        />
      )}
    </CardContainer>
  );
};
